import React, { useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import stringify from 'json-stringify-pretty-compact';
import { getAdminAiLog, getAdminAiLogs } from '../../queries/adminAiLogs';
import { formatDateWithTimezone } from '../../utils/helper';

const ACTION_LABELS = {
  generate: 'Generate message',
  generateAnother: 'Generate another',
  refine: 'Refine',
  prospectResearch: 'Prospect research',
};

const actionLabel = (action) => ACTION_LABELS[action] || action || '—';

const display = (value) => (typeof value === 'string' && value.trim() ? value : '—');

const headerCellSx = { fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' };

const LogText = ({ label, value, mono = false }) => (
  <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
    <Typography variant='subtitle2' fontWeight={700}>
      {label}
    </Typography>
    <Box
      component='textarea'
      readOnly
      value={value}
      aria-label={label}
      spellCheck={false}
      sx={{
        flex: 1,
        minHeight: 160,
        width: '100%',
        resize: 'none',
        boxSizing: 'border-box',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' : 'inherit',
        fontSize: 13,
        lineHeight: 1.5,
        color: 'text.primary',
        bgcolor: 'background.paper',
        '&:focus': { outline: '1px solid', outlineColor: 'primary.main' },
      }}
    />
  </Box>
);

const AdminAiLogsPage = () => {
  const theme = useTheme();
  const stacked = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedId, setSelectedId] = useState(null);

  const { data, isLoading, isError } = useQuery(getAdminAiLogs());
  const logs = data?.logs || [];
  const {
    data: detail,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useQuery({
    ...getAdminAiLog(selectedId),
    enabled: Boolean(selectedId),
  });
  let requestText = '';
  if (detail?.request) {
    try {
      requestText = stringify(JSON.parse(detail.request), { indent: 2, maxLength: 0 });
    } catch {
      requestText = detail.request;
    }
  }

  return (
    <Box
      sx={{
        height: stacked ? 'auto' : '100%',
        minHeight: stacked ? '100%' : 0,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        px: { xs: 2, md: 3 },
        py: { xs: 2, md: 3 },
        gap: 2,
      }}
    >
      <Typography variant='h5' fontWeight={700}>
        AI Logs
      </Typography>

      {isLoading && (
        <Box display='flex' justifyContent='center' py={10}>
          <CircularProgress aria-label='Loading AI logs' />
        </Box>
      )}

      {isError && (
        <Alert severity='error' sx={{ borderRadius: 2 }}>
          Could not load AI logs. Refresh the page and try again.
        </Alert>
      )}

      {!isLoading && !isError && (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: stacked ? 'column' : 'row',
            gap: 2,
          }}
        >
          <TableContainer
            sx={{
              flex: stacked ? 'none' : 1.15,
              minWidth: 0,
              minHeight: 0,
              maxHeight: stacked ? 360 : 'none',
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.paper',
            }}
          >
            <Table size='small' stickyHeader sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Date / time</TableCell>
                  <TableCell sx={headerCellSx}>Generated by</TableCell>
                  <TableCell sx={headerCellSx}>Client</TableCell>
                  <TableCell sx={headerCellSx}>Product</TableCell>
                  <TableCell sx={headerCellSx}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align='center' sx={{ py: 6, color: 'text.secondary' }}>
                      No AI logs yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => {
                    const selected = log.id === selectedId;
                    return (
                      <TableRow
                        key={log.id}
                        hover
                        selected={selected}
                        onClick={() => setSelectedId(log.id)}
                        sx={{
                          cursor: 'pointer',
                          '&.Mui-selected': { bgcolor: 'rgba(25, 118, 210, 0.08)' },
                          '&.Mui-selected:hover': { bgcolor: 'rgba(25, 118, 210, 0.12)' },
                        }}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateWithTimezone(log.createdAt)}</TableCell>
                        <TableCell>{display(log.userName)}</TableCell>
                        <TableCell>{display(log.clientName)}</TableCell>
                        <TableCell>{display(log.productName)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{actionLabel(log.action)}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: stacked ? 480 : 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.paper',
              p: 2,
            }}
          >
            <Box>
              <Typography variant='h6' fontWeight={700}>
                AI Request Details
              </Typography>
              {detail && selectedId === detail.id && (
                <>
                  <Typography variant='body2' mt={1}>
                    Action: {actionLabel(detail.action)}
                  </Typography>
                  <Typography variant='body2' sx={{ wordBreak: 'break-all' }}>
                    Model: {detail.model || '—'}
                  </Typography>
                  {detail.error ? (
                    <Typography variant='body2' color='error' sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                      Error: {detail.error}
                    </Typography>
                  ) : null}
                </>
              )}
            </Box>

            {!selectedId && (
              <Typography variant='body2' color='text.secondary'>
                Select a row to view the AI request and response.
              </Typography>
            )}

            {selectedId && isDetailLoading && (
              <Box display='flex' justifyContent='center' py={6}>
                <CircularProgress aria-label='Loading AI request' />
              </Box>
            )}

            {selectedId && isDetailError && (
              <Alert severity='error' sx={{ borderRadius: 2 }}>
                Could not load this AI request. Select the row again.
              </Alert>
            )}

            {detail && selectedId === detail.id && !isDetailLoading && !isDetailError && (
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <LogText label='Full AI Request' value={requestText} mono />
                <LogText label='AI Response' value={detail.response || ''} />
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AdminAiLogsPage;
